import React, { useState } from 'react';
import classes from './MainButton.module.scss';
import colors from '../../../scss/colors';
import { buttonVariants } from './types';
import { CircularProgress } from '@mui/material';

export default function MainButton({
  size = 'small',
  variant = buttonVariants.PRIMARY,
  outlined = false,
  rounded = false,
  customRadius = false,
  action = () => {},
  forceWidth,
  disabled,
  children,
  sx,
  customClass = '',
  pending = false,
  onHoverChange = () => {},
  onFocus = () => {},
}) {
  const [isHovered, setIsHovered] = useState(false);

  const sizes = {
    xsmall: classes.xsmall,
    small: classes.small,
    xmedium: classes.xmedium,
    medium: classes.medium
  };

  const roundedBySize = {
    xsmall: '30px',
    small: '40px',
    xmedium: '44px',
    medium: '56px'
  };

  const variants = {
    primary: {
      text: colors.WHITE,
      border: colors.NAVY_BLUE,
      background: colors.NAVY_BLUE
    },
    redPrimary: {
      text: colors.WHITE,
      border: colors.RED_PRIMARY,
      background: colors.RED_PRIMARY
    },
    secondary: {
      text: colors.WHITE,
      border: colors.LIGHT_BLUE,
      background: colors.LIGHT_BLUE
    },
    ternary: {
      text: colors.DARKEST_GREY,
      border: colors.DARKEST_GREY,
      background: colors.WHITE,
    },
    navyWhite: {
      text: colors.NAVY_BLUE,
      border: colors.LIGHTER_GREY,
      background: colors.WHITE,
      boxShadow: '0px 2px 6px 0px rgba(0, 0, 0, 0.03)'
    },
    navyBlue: {
      text: colors.WHITE_TXT,
      border: colors.TRANSPARENT,
      background: colors.NAVY_BLUE
    },
    lightGrey: {
      text: colors.WHITE_TXT,
      border: colors.TRANSPARENT,
      background: colors.LIGHTEST_GRAY
    },
    selected: {
      text: colors.WHITE,
      border: colors.NAVY_BLUE,
      background: colors.NAVY_BLUE
    },
    disabled: {
      text: colors.WHITE,
      border: colors.MEDIUM_GREY_V2,
      background: colors.MEDIUM_GREY_V2
    },
    text: {
      text: colors.MEDIUM_GREY,
      border: colors.TRANSPARENT,
      background: colors.TRANSPARENT
    },
    lightBlue: {
      text: colors.WHITE,
      border: colors.TRANSPARENT,
      background: colors.LIGHT_BLUE
    },
    textBlue: {
      text: colors.LIGHT_BLUE,
      border: colors.TRANSPARENT,
      background: colors.TRANSPARENT
    }
  };

  const getHoverStyles = (variantType, isOutlined) => {
    const original = variants[variantType];
    if (!original) return {};

    if (variantType === buttonVariants.TEXT || variantType === buttonVariants.TEXTBLUE) {
      return {
        color: colors.NAVY_BLUE,
        border: colors.TRANSPARENT
      }
    }

    if (isOutlined) {
      return {
        background: original.border,
        color: colors.WHITE,
        border: `1px solid ${original.border}`,
      };
    }

    return {
      background: colors.WHITE,
      color: original.background,
      border: `1px solid ${original.background}`
    };
  };

  const getVariants = (variantType, isOutlined) => {
    if (isOutlined) {
      return {
        background: colors.WHITE,
        color:
          variants[variantType]?.text === colors.WHITE
            ? variants[variantType]?.background
            : variants[variantType]?.text || colors.NAVY_BLUE,
        border: `1px solid ${variants[variantType]?.border}`,
        opacity: variants[variantType]?.opacity,
        textShadow: 'none',
        fontWeight: variantType === buttonVariants.SECONDARY ? 'bold' : 'normal'
      };
    }

    return {
      background: variants[variantType]?.background,
      color: variants[variantType]?.text,
      border: `1.5px solid ${variants[variantType]?.border}` || 'none',
      boxShadow: variants[variantType]?.boxShadow || 'none',
      textShadow: variantType === buttonVariants.SECONDARY ? '0 1px 2px rgba(0, 0, 0, 0.4)' : 'none'
    };
  };

  const getBorderRadius = () => {
    if (rounded) return roundedBySize[size];
    if (customRadius) return customRadius;

    return '5px';
  };

  return (
    <button
      className={`${classes.basic} ${sizes[size]} ${customClass} cursor-pointer`}
      style={{
        ...(isHovered
          ? getHoverStyles(variant, outlined)
          : getVariants(variant, outlined)),
        borderRadius: getBorderRadius(),
        width: forceWidth || 'fit-content',
        transition: 'all 0.3s ease',
        ...sx
      }}
      disabled={disabled}
      onFocus={onFocus}
      onClick={() => {
        action();
        setIsHovered(false);
      }}
      onMouseEnter={() => {
        setIsHovered(true);
        onHoverChange(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        onHoverChange(false);
      }}
    >
      {pending ? (
        <CircularProgress
          size={24}
          sx={{
            color: isHovered ? colors.LIGHT_BLUE : outlined ? colors.NAVY_BLUE : colors.WHITE
          }}
        />
      ) : (
        children
      )}
    </button>
  );
}
